import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { BeatLoader } from 'react-spinners';

/* ─── Styles matching BabbleOn dark theme ─── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  padding: 2rem 1rem;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  text-align: center;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.15);
`;

const LogoText = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #f0abfc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatusText = styled.p`
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
`;

const ErrorText = styled.p`
  font-size: 0.88rem;
  color: #fca5a5;
  line-height: 1.5;
`;

const SuccessText = styled.p`
  font-size: 0.92rem;
  color: #6ee7b7;
  line-height: 1.6;
`;

const ReturnLink = styled.a`
  display: inline-block;
  padding: 0.7rem 2rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 20px rgba(99,102,241,0.45);
  }
`;

/**
 * /line-join/:inviteId
 * 
 * Flow:
 * 1. User taps invite link shared from LINE
 * 2. This page checks for LINE auth code in URL params
 *    - If no code: initiates LINE OAuth, passing inviteId in state
 *    - If code present: calls /auth/line-invite to create session, then redirects to LINE
 */
const LineJoinRedirect = () => {
    const { inviteId } = useParams();
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [lineRedirectUrl, setLineRedirectUrl] = useState(null);
    const [username, setUsername] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (code && state) {
            // Step 2: We're back from LINE OAuth — complete the join
            completeJoin(code);
        } else {
            // Step 1: Initiate LINE OAuth
            initiateLineAuth();
        }
    }, []);

    const initiateLineAuth = () => {
        const clientId = process.env.REACT_APP_LINE_LOGIN_CHANNEL_ID;
        const redirectUri = encodeURIComponent('https://chat.babbleon.ai/auth/line');
        const state = `${uuidv4()}?lineJoinInviteId=${inviteId}`;
        sessionStorage.setItem('line_join_state', state);
        sessionStorage.setItem('line_join_invite_id', inviteId);

        const authUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=profile%20openid`;
        window.location.href = authUrl;
    };

    const completeJoin = async (code) => {
        try {
            setStatus('joining');

            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/auth/line-invite`,
                { code, inviteId },
                { withCredentials: true }
            );

            if (response.data.success) {
                setUsername(response.data.username);

                if (response.data.lineRedirectUrl) {
                    setLineRedirectUrl(response.data.lineRedirectUrl);
                    setStatus('redirecting');

                    // Auto-redirect after a short delay so user sees the success state
                    setTimeout(() => {
                        window.location.href = response.data.lineRedirectUrl;
                    }, 1500);
                } else {
                    // No LINE redirect URL configured — show manual instruction
                    setStatus('success_manual');
                }
            } else {
                setError(response.data.error || 'Failed to join the Babble.');
                setStatus('error');
            }
        } catch (err) {
            console.error('LINE join error:', err);
            setError('Something went wrong. Please try again.');
            setStatus('error');
        }
    };

    return (
        <PageWrapper>
            <Card>
                <LogoRow>
                    <LogoImage src="/babblers/BabbleonLogo.png" alt="BabbleOn" />
                    <LogoText>BabbleOn</LogoText>
                </LogoRow>

                {status === 'loading' && (
                    <>
                        <BeatLoader color="#a5b4fc" size={12} />
                        <StatusText>Connecting to LINE...</StatusText>
                    </>
                )}

                {status === 'joining' && (
                    <>
                        <BeatLoader color="#a5b4fc" size={12} />
                        <StatusText>Setting up your translation session...</StatusText>
                    </>
                )}

                {status === 'redirecting' && (
                    <>
                        <SuccessText>
                            ✅ You're connected, {username}!
                        </SuccessText>
                        <StatusText>
                            Taking you back to LINE...
                        </StatusText>
                        <BeatLoader color="#6ee7b7" size={8} />
                        {lineRedirectUrl && (
                            <ReturnLink href={lineRedirectUrl}>
                                Open LINE Chat
                            </ReturnLink>
                        )}
                    </>
                )}

                {status === 'success_manual' && (
                    <>
                        <SuccessText>
                            ✅ You're connected, {username}!
                        </SuccessText>
                        <StatusText>
                            Go back to LINE and send a message to the BabbleOn bot — your messages will be translated automatically.
                        </StatusText>
                    </>
                )}

                {status === 'error' && (
                    <ErrorText>{error}</ErrorText>
                )}
            </Card>
        </PageWrapper>
    );
};

export default LineJoinRedirect;
